import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { isMessageDescriptor } from '../form-components/helpers';
import CircleClose from '../icons/CircleClose';

export interface IPopupable {
    closePopup?: () => void;
}

interface IPopupProps {
    closePopup: () => void;
    visible: boolean;
    title?: string | MessageDescriptor;
    titleClassName?: string;
}

const Popup: React.FC<IPopupProps> = ({ children, closePopup, title, titleClassName, visible }) => {
    const popupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutsidePopup = (event: Event & { target: HTMLElement }) => {
            event.stopPropagation();
    
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                closePopup();
            }
        };
        document.addEventListener('mousedown', handleClickOutsidePopup);

        return () => {
            document.removeEventListener('mousedown', handleClickOutsidePopup);
        };
    }, []);

    return ReactDOM.createPortal(
        <AnimatePresence>
            {visible ? (
                <div className="popup">
                    <motion.div
                        className="popup-background"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.6 }}
                        transition={{ type: 'spring', stiffness: 170, damping: 26 }}
                        exit={{
                            opacity: 0,
                            transition: { type: 'spring', stiffness: 200, damping: 20 },
                        }}
                    />
                    <motion.div
                        className="popup-body"
                        role="dialog"
                        ref={popupRef}
                        initial={{ opacity: 0, transform: `translate(-50%, -50%) scale(0.8)` }}
                        animate={{ opacity: 1, transform: `translate(-50%, -50%) scale(1)` }}
                        transition={{ type: 'spring', stiffness: 170, damping: 26 }}
                        exit={{
                            opacity: 0,
                            transform: `translate(-50%, -50%) scale(0.8)`,
                            transition: { type: 'spring', stiffness: 300, damping: 30 },
                        }}
                    >
                        <div className="popup-body__title-wrapper">
                            {title && (
                                <h2
                                    className={`popup-body__title ${
                                        titleClassName ? titleClassName : ''
                                    }`}
                                >
                                    {isMessageDescriptor(title) ? (
                                        <FormattedMessage {...title} />
                                    ) : (
                                        title
                                    )}
                                </h2>
                            )}
                            <button className="popup-body__close-icon icon" onClick={closePopup}>
                                <CircleClose />
                            </button>
                        </div>
                        {React.Children.map(children, (child) =>
                            React.isValidElement<IPopupable>(child)
                                ? React.cloneElement<IPopupable>(child, {
                                      closePopup: () => closePopup(),
                                  })
                                : child
                        )}
                    </motion.div>
                </div>
            ) : null}
        </AnimatePresence>,
        document.querySelector('.App__container') || document.body
    );
};

export default Popup;
